<template>
  <div
    class="base-block-tmp"
    :class="[sizeClass, layoutClass, { 'small-card-type': smallCardType }, typeClass]"
  >
    <div
      class="base-block-tmp__inner-wrapper"
      :class="{
        'row-noGutter': !isLayout.mediaTop,
        'row-column-noGutter': isLayout.mediaTop,
        'saved-collection-link-card': isLinkCard,
      }"
      @click="simulateClickOnLink"
    >
      <div
        :class="{
          'row-noGutter col-12': isLayout.mediaRightSmall,
          'col-last row-noGutter': isLayout.mediaTop,
          'col-6': isLayout.mediaLeft,
          'col-7': isLayout.mediaRightSecondary,
          'row-column-noGutter': isLayout.mediaLeft || isLayout.mediaRightSecondary,
        }"
      >
        <div
          class="row-noGutter"
          :class="{
            col: isLayout.mediaLeft || isLayout.mediaRightSecondary,
            'col-12': isLayout.mediaTop || isLayout.mediaRightSmall,
          }"
        >
          <div
            class="base-block-tmp__content-slot row-noGutter"
            :class="{
              'col-6': isLayout.mediaRightSmall,
              'col-12': isLayout.mediaRightSecondary || isLayout.mediaLeft,
            }"
          >
            <div class="base-block-tmp__content-slot__padding">
              <slot name="content" />
            </div>
            <slot
              v-if="isLayout.mediaRightSmall"
              name="footer"
            />
          </div>
          <div
            v-if="isLayout.mediaRightSmall"
            class="base-block-tmp__media col-6"
          >
            <slot name="media" />
          </div>
        </div>
        <slot
          v-if="!isLayout.mediaRightSmall"
          name="footer"
        />
      </div>
      <div
        v-if="!isLayout.mediaRightSmall"
        class="base-block-tmp__media-lg"
        :class="{
          col: isLayout.mediaTop,
          'col-5': isLayout.mediaRightSecondary,
          'col-6-first': isLayout.mediaLeft,
        }"
      >
        <slot name="media" />
      </div>
    </div>
  </div>
</template>

<script>

import kebabcase from 'lodash.kebabcase'
export const layoutTypes = {
  mediaTop: 'mediaTop',
  mediaLeft: 'mediaLeft',
  mediaRightSecondary: 'mediaRightSecondary',
  mediaRightSmall: 'mediaRightSmall',
}

export default {
  name: 'BaseBlockComponent',
  props: {
    labelText: {
      type: String,
      default: 'Länkblock',
    },
    url: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'quarter',
      validator (value) {
        return ['full', 'half', 'third', 'quarter'].includes(value)
      },
    },
    centerContent: {
      type: Boolean,
      default: true,
    },
    layoutType: {
      type: String,
      default: layoutTypes.mediaTop,
      validator (value) {
        return Object.keys(layoutTypes).includes(value)
      },
    },
    type: {
      type: String,
      required: true,
      validator (value) {
        return ['recipe', 'recipeCollection', 'recipeSavedCollection', 'article'].includes(value)
      },
    },
    isLinkCard: {
      type: Boolean,
      default: false,
    },
    smallCardType: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isSize () {
      return {
        [this.size]: true,
      }
    },
    isType () {
      return {
        [this.type]: true,
      }
    },
    isLayout () {
      return {
        [this.layoutType]: true,
      }
    },
    sizeClass () {
      return `base-block-tmp--${this.size}`
    },
    layoutClass () {
      return `base-block-tmp--${kebabcase(this.layoutType)}`
    },
    typeClass () {
      return `${kebabcase(this.type)}-card--${this.size}`
    },
  },
  mounted () {
    if (!!window.MSInputMethodContext && !!document.documentMode && !document.body.hasAttribute('ie11')) {
      document.body.setAttribute('ie11', '')
    }
  },
  methods: {
    simulateClickOnLink () {
      if (this.url) {
        this.$emit('simulateClick')
      }
    }
  },
}
</script>

<style lang="scss">
$base-min-height: 9rem;
$base-height-up-md: 25rem;
$saved-collection-height-up-md: 20rem;
$base-height-up-lg: 32.5rem;
$saved-collection-height-up-lg: 26.5rem;

[class*='row-']:not([class*='col-']) {
  flex-basis: auto;
}

.base-block-tmp {
  $base: &;

  box-shadow: $box-shadow-primary;
  cursor: pointer;

  --min-height: 9rem;
  --sm-height: 100%;
  --md-height: 25rem;
  --lg-height: 32.5rem;
  --xl-height: 32.5rem;

  // Collection
  &.recipe-collection-card {
    &--full {
      --min-height: 23.75rem;
      --sm-height: 23.75rem;
      --md-height: 25rem;
      --xl-height: 32.5rem;
    }

    &--half {
      --min-height: 7rem;
      --md-height: 25rem;
      --lg-height: 32.5rem;
      --xl-height: 32.5rem;
    }

    &--third,
    &--quarter {
      --min-height: 7rem;
      --md-height: 25rem;
      --lg-height: 25.5rem;
      --xl-height: 25.5rem;
    }

    &.small-card-type {
      --md-height: 20rem;
      --lg-height: 20rem;
      --xl-height: 20rem;
    }
  }
  // Saved Collection
  &.recipe-saved-collection-card {
    &--full,
    &--half,
    &--third,
    &--quarter {
      --min-height: 9rem;
      --md-height: 19.5rem;
      --lg-height: 25.5rem;
      --xl-height: 25.5rem;
    }

    &.small-card-type {
      --lg-height: 19.5rem;
      --xl-height: 19.5rem;
    }
  }
  // Recipe
  &.recipe-card {
    &--full {
      --min-height: 22.25rem;
      --md-height: 22.5rem;
      --lg-height: 30.5rem;
      --xl-height: 28.875rem;
    }

    &--half {
      --min-height: 11rem;
      --md-height: 23.75rem;
      --lg-height: 30.75rem;
      --xl-height: 29.625rem;
    }

    &--third {
      --min-height: 11rem;
      --md-height: 21.625rem;
      --lg-height: 27rem;
      --xl-height: 26rem;
    }

    &--quarter {
      --min-height: 11rem;
      --md-height: 17.75rem;
      --lg-height: 21.75rem;
      --xl-height: 25.75rem;
    }

    &.small-card-type {
      --md-height: 17.75rem;
      --lg-height: 17.75rem;
      --xl-height: 17.75rem;
    }
  }

  // Article
  &.article-card {
    &--full {
      --min-height: 23.75rem;
      --sm-height: 30.5rem;
      --md-height: 25rem;
      --xl-height: 32.5rem;
    }

    &--half {
      --min-height: 7rem;
      --sm-height: 30.5rem;
      --md-height: 25rem;
      --lg-height: 32.5rem;
      --xl-height: 32.5rem;
    }

    &--third,
    &--quarter {
      --min-height: 7rem;
      --md-height: 25rem;
      --lg-height: 32.5rem;
      --xl-height: 32.5rem;
    }

    &.small-card-type {
      --md-height: 20rem;
      --lg-height: 20rem;
      --xl-height: 20rem;
    }
  }

  h3 {
    margin: 0;
  }

  > a {
    color: inherit;

    &:hover,
    &:active {
      color: inherit;
    }
  }

  &:hover {
    box-shadow: 0.06rem 0.1225rem 0.225rem 0 rgba(0, 0, 0, 0.06);
  }

  &__content-slot {
    width: 100%;
    text-align: center;

    &__padding {
      width: 100%;
      padding: 0.75rem 1rem 1rem;

      @include media-breakpoint-up(lg) {
        padding: 0.75rem 1.5rem 1rem;
      }

      #{$base}--full & {
        padding: 1.5rem 1.5rem;

        @include media-breakpoint-up(md) {
          margin: auto;
          padding: 3rem;
        }
      }

      .base-block-tmp.base-block-tmp--full .base-block-tmp__content-slot & {
        @include media-breakpoint-up(lg) {
          padding: 4.5rem 4rem;
        }

        @include media-breakpoint-up(xl) {
          padding: 5.5rem 5rem;
        }
      }
    }
  }

  &__inner-wrapper {
    border-radius: 4px;
    background: $color-white;
    flex-wrap: nowrap;
    height: var(--sm-height);
    min-height: var(--min-height);

    @include media-breakpoint-up(md) {
      height: var(--md-height);

      .base-block-tmp__content-slot,
      .base-block-tmp__content-slot__padding {
        max-height: var(--md-height);
      }
    }

    #{$base} & {
      @include media-breakpoint-up(lg) {
        height: var(--lg-height);

        .base-block-tmp__content-slot,
        .base-block-tmp__content-slot__padding {
          max-height: var(--lg-height);
          overflow: hidden;
        }
      }

      @include media-breakpoint-up(xl) {
        height: var(--xl-height);

        .base-block-tmp__content-slot,
        .base-block-tmp__content-slot__padding {
          max-height: var(--xl-height);
          overflow: hidden;
        }
      }
    }

    #{$base}--full & {
      @include media-breakpoint-up(md) {
        height: auto;
        min-height: var(--md-height);

        .base-block-tmp__content-slot,
        .base-block-tmp__content-slot__padding {
          max-height: 100%;
        }
      }

      @include media-breakpoint-up(lg) {
        height: auto;
        min-height: var(--lg-height);

        .base-block-tmp__content-slot,
        .base-block-tmp__content-slot__padding {
          max-height: 100%;
        }
      }

      @include media-breakpoint-up(xl) {
        height: auto;
        min-height: var(--xl-height);

        .base-block-tmp__content-slot,
        .base-block-tmp__content-slot__padding {
          max-height: 100%;
        }
      }
    }
  }

  // Default font sizes - third & quarter
  .base-block-tmp__content-slot {
    [class$='__prefix'] {
      @include font(hand, 1, 'normal');

      color: $color-red;
    }

    [class$='__title'] {
      overflow-wrap: break-word;
      word-wrap: break-word;
      hyphens: auto;

      @include font(rubrik, 2, 'bold');
    }

    [class$='__preamble'] {
      @include font(text, 3, 'light');
    }
  }

  // Font sizes full
  &--full {
    .base-block-tmp__content-slot {
      [class$='__prefix'] {
        @include font(hand, 2, 'normal');
      }

      [class$='__title'] {
        @include font(rubrik, 3, 'bold');
      }

      [class$='__preamble'] {
        margin-top: 1.5rem;
        max-height: 100%;
        overflow: hidden;

        @include media-breakpoint-up(lg) {
          @include font(text, 4, 'light');
        }
      }
    }

    @include media-breakpoint-up(lg) {
      .base-block-tmp {
        &__content-slot,
        &__content-slot__padding {
          max-height: none;
          margin: auto;
        }
      }
    }
  }

  &__media .epi-picture {
    height: 100%;

    div {
      border-radius: 4px;
      height: 100%;
    }
  }

  & &__media-lg {
    width: 100%;
    flex-basis: auto;
    flex-grow: 1;
    display: flex;

    > div {
      width: 100%;
    }

    .epi-picture {
      flex-grow: 1;
      display: flex;

      > div {
        flex-grow: 1;
        display: flex;
      }

      @at-root {
        @include media-breakpoint-down(sm) {
          #{$base} .base-block-tmp__inner-wrapper &,
          #{$base} .base-block-tmp__inner-wrapper {
            border-radius: 0 0 0 0;
          }
        }
      }
    }
  }

  @include media-breakpoint-up(md) {
    &#{$base}--media-top #{$base}__media-lg .epi-picture {
      border-radius: 4px 4px 0 0;
    }

    &#{$base}--media-left #{$base}__media-lg .epi-picture {
      border-radius: 4px 0 0 4px;
    }

    &#{$base}--media-right-secondary #{$base}__media-lg .epi-picture {
      border-radius: 0 4px 4px 0;
    }
  }
}
</style>
